import { useState } from 'react';

import { useMutation, useQuery } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router';

import * as api from '../api/loginApi';
import { useLoginFlow } from '../contexts/LoginFlowContext';
import type { TwoFactorProvider, TwoFactorStep } from '../types/login';

export const useTwoFactor = () => {
    const intl = useIntl();
    const navigate = useNavigate();
    const { returnUrl } = useLoginFlow();

    const [step, setStep] = useState<TwoFactorStep>('provider-select');
    const [selectedProvider, setSelectedProvider] = useState<TwoFactorProvider | null>(null);
    const [rememberBrowser, setRememberBrowser] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | undefined>();

    // -----------------------------------------------------------------------
    // Fetch available 2FA providers
    // -----------------------------------------------------------------------

    const providersQuery = useQuery({
        queryKey: ['twoFactorProviders'],
        queryFn: api.getTwoFactorProviders,
        select: (data) => {
            if (data.success) {
                return data.providers ?? [];
            }
            if (data.invalidCookie) {
                setErrorMessage(
                    intl.formatMessage({
                        id: 'login.twoFactor.error.sessionExpired',
                    }),
                );
            }
            return [];
        },
    });

    // -----------------------------------------------------------------------
    // Send verification code
    // -----------------------------------------------------------------------

    const sendCodeMutation = useMutation({
        mutationFn: (provider: string) => api.sendTwoFactorCode({ provider }),
        onSuccess: (data) => {
            if (data.success) {
                setStep('verify-code');
            } else {
                setErrorMessage(
                    data.errorMessage ??
                        intl.formatMessage({
                            id: 'login.twoFactor.error.sendCodeFailed',
                        }),
                );
            }
        },
    });

    // -----------------------------------------------------------------------
    // Verify code
    // -----------------------------------------------------------------------

    const verifyCodeMutation = useMutation({
        mutationFn: async (code: string) => {
            if (!selectedProvider) {
                throw new Error('No provider selected');
            }
            return api.verifyTwoFactorCode({
                provider: selectedProvider,
                code,
                rememberBrowser,
                returnUrl,
            });
        },
        onSuccess: (data) => {
            if (data.success && data.returnUrl) {
                window.location.href = data.returnUrl;
            } else if (data.lockedOut) {
                setErrorMessage(
                    intl.formatMessage({
                        id: 'login.twoFactor.error.lockedOut',
                        defaultMessage:
                            'Your account is temporarily locked as a result of too many failed login attempts. Please try again in a few minutes.',
                    }),
                );
            } else {
                setErrorMessage(
                    intl.formatMessage({
                        id: 'login.twoFactor.error.invalidCode',
                        defaultMessage: 'The code you have entered is not valid',
                    }),
                );
            }
        },
    });

    // -----------------------------------------------------------------------
    // Public API
    // -----------------------------------------------------------------------

    const selectProvider = (provider: TwoFactorProvider) => {
        setSelectedProvider(provider);
        setErrorMessage(undefined);
    };

    const sendCode = () => {
        if (selectedProvider) {
            setErrorMessage(undefined);
            sendCodeMutation.mutate(selectedProvider);
        }
    };

    const verifyCode = (code: string) => {
        setErrorMessage(undefined);
        verifyCodeMutation.mutate(code);
    };

    const goToPhoneSetup = () => {
        setStep('phone-setup');
    };

    const backToProviderSelect = () => {
        setStep('provider-select');
        setErrorMessage(undefined);
    };

    const backToLogin = () => {
        navigate('/');
    };

    const onPhoneSetupComplete = () => {
        providersQuery.refetch();
        setStep('provider-select');
    };

    return {
        step,
        providers: providersQuery.data ?? [],
        selectedProvider,
        rememberBrowser,
        isLoadingProviders: providersQuery.isLoading,
        isSendingCode: sendCodeMutation.isPending,
        sendCodeError: errorMessage ?? sendCodeMutation.error?.message,
        isVerifyingCode: verifyCodeMutation.isPending,
        verifyCodeError: errorMessage ?? verifyCodeMutation.error?.message,
        selectProvider,
        sendCode,
        verifyCode,
        setRememberBrowser,
        goToPhoneSetup,
        backToProviderSelect,
        backToLogin,
        onPhoneSetupComplete,
    };
};
